import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Check,
  Loader2,
  BarChart3,
  Search,
  ShoppingBag,
  Megaphone,
  Globe,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getAnalyticsProperties,
  getSearchConsoleSites,
  getMerchantAccounts,
  AnalyticsProperty,
  SearchConsoleSite,
  MerchantAccount,
} from '../services/googleOAuthService';

interface PropertySelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedItems: SelectedProperty[]) => void;
  platformId: string;
  userId: string;
  connectionId: string;
  googleEmail: string;
}

export interface SelectedProperty {
  type: 'analytics' | 'search-console' | 'merchant' | 'ads';
  id: string;
  name: string;
  accountId?: string;
  accountName?: string;
}

const platformConfig: Record<string, { icon: typeof BarChart3; color: string; title: string; titleEn: string }> = {
  'google-analytics': {
    icon: BarChart3,
    color: '#E37400',
    title: 'Analytics Property Seçin',
    titleEn: 'Select Analytics Property',
  },
  'google-search-console': {
    icon: Search,
    color: '#458CF5',
    title: 'Site Seçin',
    titleEn: 'Select Site',
  },
  'google-merchant': {
    icon: ShoppingBag,
    color: '#4285F4',
    title: 'Merchant Hesabı Seçin',
    titleEn: 'Select Merchant Account',
  },
  'google-ads': {
    icon: Megaphone,
    color: '#4285F4',
    title: 'Ads Hesabı Seçin',
    titleEn: 'Select Ads Account',
  },
};

export function PropertySelectModal({
  isOpen,
  onClose,
  onSelect,
  platformId,
  userId,
  connectionId,
  googleEmail,
}: PropertySelectModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<AnalyticsProperty[]>([]);
  const [sites, setSites] = useState<SearchConsoleSite[]>([]);
  const [merchants, setMerchants] = useState<MerchantAccount[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const config = platformConfig[platformId] || platformConfig['google-analytics'];
  const Icon = config.icon;

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, platformId, userId, connectionId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (platformId === 'google-analytics') {
        const data = await getAnalyticsProperties(userId, connectionId);
        setProperties(data);
      } else if (platformId === 'google-search-console') {
        const data = await getSearchConsoleSites(userId, connectionId);
        setSites(data);
      } else if (platformId === 'google-merchant') {
        const data = await getMerchantAccounts(userId, connectionId);
        setMerchants(data);
      }
    } catch (err) {
      setError(isEnglish ? 'Failed to load data' : 'Veriler yüklenemedi');
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleConfirm = () => {
    const selected: SelectedProperty[] = [];

    if (platformId === 'google-analytics') {
      properties
        .filter((p) => selectedItems.has(p.propertyId))
        .forEach((p) => {
          selected.push({
            type: 'analytics',
            id: p.propertyId,
            name: p.propertyName,
            accountId: p.accountId,
            accountName: p.accountName,
          });
        });
    } else if (platformId === 'google-search-console') {
      sites
        .filter((s) => selectedItems.has(s.siteUrl))
        .forEach((s) => {
          selected.push({
            type: 'search-console',
            id: s.siteUrl,
            name: s.siteUrl,
          });
        });
    } else if (platformId === 'google-merchant') {
      merchants
        .filter((m) => selectedItems.has(m.merchantId))
        .forEach((m) => {
          selected.push({
            type: 'merchant',
            id: m.merchantId,
            name: `Merchant ID: ${m.merchantId}`,
          });
        });
    }

    onSelect(selected);
    onClose();
  };

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent mb-4" />
          <p className="text-text-secondary">
            {isEnglish ? 'Loading your accounts...' : 'Hesaplarınız yükleniyor...'}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            {isEnglish ? 'Try Again' : 'Tekrar Dene'}
          </button>
        </div>
      );
    }

    // Analytics Properties
    if (platformId === 'google-analytics') {
      if (properties.length === 0) {
        return (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="text-text-secondary">
              {isEnglish
                ? 'No Analytics properties found for this account'
                : 'Bu hesap için Analytics property bulunamadı'}
            </p>
          </div>
        );
      }

      // Group by account
      const groupedByAccount = properties.reduce((acc, prop) => {
        if (!acc[prop.accountId]) {
          acc[prop.accountId] = { name: prop.accountName, properties: [] };
        }
        acc[prop.accountId].properties.push(prop);
        return acc;
      }, {} as Record<string, { name: string; properties: AnalyticsProperty[] }>);

      return (
        <div className="space-y-4">
          {Object.entries(groupedByAccount).map(([accountId, account]) => (
            <div key={accountId} className="space-y-2">
              <h4 className="text-sm font-medium text-text-secondary px-2">
                {account.name}
              </h4>
              {account.properties.map((prop) => (
                <button
                  key={prop.propertyId}
                  onClick={() => toggleSelection(prop.propertyId)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
                    selectedItems.has(prop.propertyId)
                      ? 'border-accent bg-accent/10'
                      : 'border-border-default bg-bg-elevated hover:border-accent/50'
                  )}
                >
                  <div
                    className={cn(
                      'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                      selectedItems.has(prop.propertyId)
                        ? 'border-accent bg-accent'
                        : 'border-text-tertiary'
                    )}
                  >
                    {selectedItems.has(prop.propertyId) && (
                      <Check size={12} className="text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">
                      {prop.propertyName}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      Property ID: {prop.propertyId}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-text-tertiary flex-shrink-0" />
                </button>
              ))}
            </div>
          ))}
        </div>
      );
    }

    // Search Console Sites
    if (platformId === 'google-search-console') {
      if (sites.length === 0) {
        return (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="text-text-secondary">
              {isEnglish
                ? 'No Search Console sites found for this account'
                : 'Bu hesap için Search Console sitesi bulunamadı'}
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {sites.map((site) => (
            <button
              key={site.siteUrl}
              onClick={() => toggleSelection(site.siteUrl)}
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
                selectedItems.has(site.siteUrl)
                  ? 'border-accent bg-accent/10'
                  : 'border-border-default bg-bg-elevated hover:border-accent/50'
              )}
            >
              <div
                className={cn(
                  'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                  selectedItems.has(site.siteUrl)
                    ? 'border-accent bg-accent'
                    : 'border-text-tertiary'
                )}
              >
                {selectedItems.has(site.siteUrl) && (
                  <Check size={12} className="text-white" />
                )}
              </div>
              <Globe size={20} className="text-text-tertiary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary truncate">{site.siteUrl}</p>
                <p className="text-xs text-text-tertiary capitalize">
                  {site.permissionLevel?.toLowerCase().replace('site', '')}
                </p>
              </div>
            </button>
          ))}
        </div>
      );
    }

    // Merchant Center Accounts
    if (platformId === 'google-merchant') {
      if (merchants.length === 0) {
        return (
          <div className="text-center py-12">
            <ShoppingBag className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="text-text-secondary">
              {isEnglish
                ? 'No Merchant Center accounts found'
                : 'Merchant Center hesabı bulunamadı'}
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {merchants.map((merchant) => (
            <button
              key={merchant.merchantId}
              onClick={() => toggleSelection(merchant.merchantId)}
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
                selectedItems.has(merchant.merchantId)
                  ? 'border-accent bg-accent/10'
                  : 'border-border-default bg-bg-elevated hover:border-accent/50'
              )}
            >
              <div
                className={cn(
                  'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                  selectedItems.has(merchant.merchantId)
                    ? 'border-accent bg-accent'
                    : 'border-text-tertiary'
                )}
              >
                {selectedItems.has(merchant.merchantId) && (
                  <Check size={12} className="text-white" />
                )}
              </div>
              <ShoppingBag size={20} className="text-text-tertiary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary">
                  Merchant ID: {merchant.merchantId}
                </p>
                {merchant.aggregatorId && (
                  <p className="text-xs text-text-tertiary">
                    Aggregator: {merchant.aggregatorId}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      );
    }

    return null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-surface border border-border-default rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-border-default">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Icon size={20} style={{ color: config.color }} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-text-primary">
              {isEnglish ? config.titleEn : config.title}
            </h3>
            <p className="text-xs text-text-tertiary">{googleEmail}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-tertiary hover:text-text-primary"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{renderContent()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border-default">
          <p className="text-sm text-text-secondary">
            {selectedItems.size} {isEnglish ? 'selected' : 'seçildi'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated transition-colors"
            >
              {isEnglish ? 'Cancel' : 'İptal'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedItems.size === 0}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                selectedItems.size > 0
                  ? 'bg-accent text-white hover:bg-accent/90'
                  : 'bg-bg-elevated text-text-tertiary cursor-not-allowed'
              )}
            >
              {isEnglish ? 'Connect' : 'Bağlan'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
